import { createSlice } from "@reduxjs/toolkit";
import { analyticsSliceInitialState } from "./analyticsSliceInitialState";
import { API_STATUS } from "../../../constants/apiStatus";
import {
  getAnalyticsAction,
  getRecentActivityAction,
  getUserStatsAction,
  getRevenueStatsAction,
  getSubscriptionStatsAction,
  getUsageStatsAction,
} from "./analyticsSliceActions";

const analyticsSlice = createSlice({
  name: "analytics",
  initialState: analyticsSliceInitialState,
  reducers: {
    // Set selected days for analytics
    setSelectedDays: (state, action) => {
      state.selectedDays = action.payload;
    },

    // Set selected weeks for recent activity
    setSelectedActivityWeeks: (state, action) => {
      state.selectedActivityWeeks = action.payload;
    },

    // Clear analytics data
    clearAnalytics: (state) => {
      state.analytics = null;
      state.error = null;
      state.apiStatus.getAnalytics = API_STATUS.IDLE;
    },

    // Clear recent activity data
    clearRecentActivity: (state) => {
      state.recentActivity = null;
      state.apiStatus.getRecentActivity = API_STATUS.IDLE;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get Analytics
    builder
      .addCase(getAnalyticsAction.pending, (state) => {
        state.apiStatus.getAnalytics = API_STATUS.STARTED;
        state.error = null;
      })
      .addCase(getAnalyticsAction.fulfilled, (state, action) => {
        state.apiStatus.getAnalytics = API_STATUS.SUCCESS;
        state.analytics = action.payload.analytics;
        state.selectedDays = action.payload.days;
        state.error = null;
      })
      .addCase(getAnalyticsAction.rejected, (state, action) => {
        state.apiStatus.getAnalytics = API_STATUS.FAILED;
        state.error = action.payload;
        state.analytics = null;
      });

    // Get User Stats
    builder
      .addCase(getUserStatsAction.pending, (state) => {
        state.apiStatus.getUserStats = API_STATUS.STARTED;
      })
      .addCase(getUserStatsAction.fulfilled, (state, action) => {
        state.apiStatus.getUserStats = API_STATUS.SUCCESS;
        if (state.analytics) {
          state.analytics.users = action.payload;
        }
      })
      .addCase(getUserStatsAction.rejected, (state, action) => {
        state.apiStatus.getUserStats = API_STATUS.FAILED;
        state.error = action.payload;
      });

    // Get Revenue Stats
    builder
      .addCase(getRevenueStatsAction.pending, (state) => {
        state.apiStatus.getRevenueStats = API_STATUS.STARTED;
      })
      .addCase(getRevenueStatsAction.fulfilled, (state, action) => {
        state.apiStatus.getRevenueStats = API_STATUS.SUCCESS;
        if (state.analytics) {
          state.analytics.revenue = action.payload;
        }
      })
      .addCase(getRevenueStatsAction.rejected, (state, action) => {
        state.apiStatus.getRevenueStats = API_STATUS.FAILED;
        state.error = action.payload;
      });

    // Get Subscription Stats
    builder
      .addCase(getSubscriptionStatsAction.pending, (state) => {
        state.apiStatus.getSubscriptionStats = API_STATUS.STARTED;
      })
      .addCase(getSubscriptionStatsAction.fulfilled, (state, action) => {
        state.apiStatus.getSubscriptionStats = API_STATUS.SUCCESS;
        if (state.analytics) {
          state.analytics.subscriptions = action.payload;
        }
      })
      .addCase(getSubscriptionStatsAction.rejected, (state, action) => {
        state.apiStatus.getSubscriptionStats = API_STATUS.FAILED;
        state.error = action.payload;
      });

    // Get Usage Stats
    builder
      .addCase(getUsageStatsAction.pending, (state) => {
        state.apiStatus.getUsageStats = API_STATUS.STARTED;
      })
      .addCase(getUsageStatsAction.fulfilled, (state, action) => {
        state.apiStatus.getUsageStats = API_STATUS.SUCCESS;
        if (state.analytics) {
          state.analytics.usage = action.payload;
        }
      })
      .addCase(getUsageStatsAction.rejected, (state, action) => {
        state.apiStatus.getUsageStats = API_STATUS.FAILED;
        state.error = action.payload;
      });

    // Get Recent Activity
    builder
      .addCase(getRecentActivityAction.pending, (state) => {
        state.apiStatus.getRecentActivity = API_STATUS.STARTED;
        state.error = null;
      })
      .addCase(getRecentActivityAction.fulfilled, (state, action) => {
        state.apiStatus.getRecentActivity = API_STATUS.SUCCESS;
        state.recentActivity = action.payload.activity;
        state.selectedActivityWeeks = action.payload.weeks;
        state.error = null;
      })
      .addCase(getRecentActivityAction.rejected, (state, action) => {
        state.apiStatus.getRecentActivity = API_STATUS.FAILED;
        state.error = action.payload;
        state.recentActivity = null;
      });
  },
});

export const {
  setSelectedDays,
  setSelectedActivityWeeks,
  clearAnalytics,
  clearRecentActivity,
  clearError,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
